import { useMemo, useState, useEffect } from 'react';
import {
  ReactFlow,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  MarkerType,
  type NodeTypes,
  type EdgeTypes,
  type Node,
  type Edge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Text } from '@backstage/ui';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import ELK, { type ElkNode, type ElkExtendedEdge } from 'elkjs/lib/elk.bundled';
import { appTranslationRef } from '../../../../core/i18n';
import { TableNode, ROW_HEIGHT, HEADER_HEIGHT } from './table-node.component';
import type { TableNodeData } from './table-node.component';
import { RelationEdge } from '../graph/relation-edge.component';
import styles from '../graph/entity-graph.module.css';
import type { DbSchemaResponse } from './db-explorer.types';

const nodeTypes: NodeTypes = { tableNode: TableNode };
const edgeTypes: EdgeTypes = { fkEdge: RelationEdge };

const MIN_NODE_WIDTH = 180;
const CHAR_WIDTH = 7; // approx char width for 11px JetBrains Mono
const NODE_H_PADDING = 20; // left + right padding (10 each)
const BADGE_WIDTH = 20; // PK/FK badge + gap
const COL_TYPE_GAP = 8;
const PADDING = 8;
const GRAPH_HEIGHT = 380;

/** Estimate node pixel width from its content. */
function estimateNodeWidth(
  tableName: string,
  columns: { name: string; type: string }[],
): number {
  const headerWidth = tableName.length * CHAR_WIDTH + NODE_H_PADDING;
  let maxRowWidth = 0;
  for (const col of columns) {
    const rowWidth =
      BADGE_WIDTH +
      col.name.length * CHAR_WIDTH +
      COL_TYPE_GAP +
      col.type.length * (CHAR_WIDTH - 1) +
      NODE_H_PADDING;
    if (rowWidth > maxRowWidth) maxRowWidth = rowWidth;
  }
  return Math.max(MIN_NODE_WIDTH, headerWidth, maxRowWidth);
}

const elk = new ELK();

interface SchemaGraphProps {
  schema: DbSchemaResponse | null;
}

function nodeHeight(colCount: number): number {
  return HEADER_HEIGHT + colCount * ROW_HEIGHT + PADDING;
}

/**
 * Build React Flow nodes and ELK input from the schema.
 * This is the synchronous part — positions are set to 0,0 and
 * will be overwritten after ELK layout completes.
 */
function buildGraphData(schema: DbSchemaResponse) {
  const fkColumns = new Set<string>();
  for (const fk of schema.foreignKeys) {
    fkColumns.add(`${fk.sourceTable}.${fk.sourceColumn}`);
  }

  // Compute per-node widths
  const nodeWidths = new Map<string, number>();
  for (const table of schema.tables) {
    nodeWidths.set(
      table.name,
      estimateNodeWidth(
        table.name,
        table.columns.map(c => ({ name: c.name, type: c.dataType })),
      ),
    );
  }

  // React Flow nodes (positions placeholder)
  const flowNodes: Node<TableNodeData>[] = schema.tables.map(table => ({
    id: table.name,
    type: 'tableNode',
    position: { x: 0, y: 0 },
    data: {
      tableName: table.name,
      width: nodeWidths.get(table.name) ?? MIN_NODE_WIDTH,
      columns: table.columns.map(col => ({
        name: col.name,
        type: col.dataType,
        isPK: col.isPrimaryKey,
        isFK: fkColumns.has(`${table.name}.${col.name}`),
      })),
    },
  }));

  // React Flow edges
  const flowEdges: Edge[] = schema.foreignKeys.map((fk, i) => ({
    id: `fk-${i}-${fk.sourceTable}-${fk.targetTable}`,
    source: fk.sourceTable,
    target: fk.targetTable,
    type: 'fkEdge',
    data: { label: `${fk.sourceColumn} \u2192 ${fk.targetColumn}` },
  }));

  // ELK graph input
  const elkGraph: ElkNode = {
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'org.eclipse.elk.layered',
      'elk.direction': 'RIGHT',
      'elk.spacing.nodeNode': '50',
      'elk.layered.spacing.nodeNodeBetweenLayers': '80',
      'elk.padding': '[top=20,left=20,bottom=20,right=20]',
      'elk.separateConnectedComponents': 'true',
      'elk.spacing.componentComponent': '60',
    },
    children: schema.tables.map(table => ({
      id: table.name,
      width: nodeWidths.get(table.name) ?? MIN_NODE_WIDTH,
      height: nodeHeight(table.columns.length),
    })),
    edges: schema.foreignKeys.map(
      (fk, i): ElkExtendedEdge => ({
        id: `elk-fk-${i}`,
        sources: [fk.sourceTable],
        targets: [fk.targetTable],
      }),
    ),
  };

  return { flowNodes, flowEdges, elkGraph };
}

export const SchemaGraph = ({ schema }: SchemaGraphProps) => {
  const { t } = useTranslationRef(appTranslationRef);

  // Pre-build the graph data (nodes/edges/elk input) synchronously
  const graphData = useMemo(() => {
    if (!schema || schema.tables.length === 0) return null;
    return buildGraphData(schema);
  }, [schema]);

  // ELK layout is async — store the positioned nodes in state
  const [layoutNodes, setLayoutNodes] = useState<Node[] | null>(null);

  useEffect(() => {
    if (!graphData) {
      setLayoutNodes(null);
      return undefined;
    }

    let cancelled = false;

    elk.layout(graphData.elkGraph).then(layoutResult => {
      if (cancelled) return;

      // Apply ELK positions to React Flow nodes
      const positionMap = new Map<string, { x: number; y: number }>();
      for (const child of layoutResult.children ?? []) {
        positionMap.set(child.id, { x: child.x ?? 0, y: child.y ?? 0 });
      }

      const positioned = graphData.flowNodes.map(node => ({
        ...node,
        position: positionMap.get(node.id) ?? node.position,
      }));

      setLayoutNodes(positioned);
    });

    return () => {
      cancelled = true;
    };
  }, [graphData]);

  // Resolve CSS variable so arrow markers match edge stroke color
  const edgeColor = useMemo(
    () =>
      getComputedStyle(document.documentElement)
        .getPropertyValue('--bui-fg-secondary')
        .trim() || 'rgba(255, 255, 255, 0.7)',
    [],
  );

  // Assign edge handles based on relative positions of laid-out nodes
  const edges = useMemo(() => {
    if (!graphData || !layoutNodes) return [];

    const posMap = new Map(layoutNodes.map(n => [n.id, n.position]));
    const marker = {
      type: MarkerType.ArrowClosed,
      width: 14,
      height: 14,
      color: edgeColor,
    };

    return graphData.flowEdges.map(edge => {
      const srcPos = posMap.get(edge.source);
      const tgtPos = posMap.get(edge.target);
      if (!srcPos || !tgtPos) return { ...edge, markerEnd: marker };

      const dx = tgtPos.x - srcPos.x;
      const dy = tgtPos.y - srcPos.y;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);

      let sourceHandle: string | undefined;
      let targetHandle: string | undefined;

      // Pick the axis with the larger delta
      if (absDx >= absDy) {
        // Horizontal
        sourceHandle = dx >= 0 ? 'right' : 'left';
        targetHandle = dx >= 0 ? 'left' : 'right';
      } else {
        // Vertical
        sourceHandle = dy >= 0 ? 'bottom' : 'top';
        targetHandle = dy >= 0 ? 'top' : 'bottom';
      }

      return { ...edge, sourceHandle, targetHandle, markerEnd: marker };
    });
  }, [graphData, layoutNodes, edgeColor]);

  // --- Render states ---

  if (!schema) {
    return (
      <Text variant="body-medium" color="secondary">
        {t('dbExplorer.schema.loading')}
      </Text>
    );
  }

  if (schema.tables.length === 0) {
    return (
      <Text variant="body-medium" color="secondary">
        {t('dbExplorer.schema.empty')}
      </Text>
    );
  }

  // ELK still computing
  if (!layoutNodes) {
    return (
      <Text variant="body-medium" color="secondary">
        {t('dbExplorer.schema.loading')}
      </Text>
    );
  }

  return (
    <div className={styles.graphContainer} style={{ height: GRAPH_HEIGHT }}>
      <ReactFlow
        defaultNodes={layoutNodes}
        defaultEdges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        colorMode="dark"
        fitView
        nodesDraggable
        nodesConnectable={false}
        elementsSelectable={false}
        autoPanSpeed={4}
        fitViewOptions={{ padding: 0.15 }}
        minZoom={0.005}
        maxZoom={1}
        proOptions={{ hideAttribution: true }}
      >
        <Controls showInteractive={false} />
        <MiniMap
          nodeColor="#272745"
          maskColor="rgba(0, 0, 0, 0.6)"
          pannable
          zoomable
        />
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1}
          color="rgba(255, 255, 255, 0.1)"
        />
      </ReactFlow>
    </div>
  );
};
